import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Minus, Trash2, User, LogOut, LayoutDashboard, Box, FolderOpen, ClipboardList, Upload, FileSpreadsheet, Calculator, MapPin, Clock, Check, AlertCircle, Gift, ChevronDown, HelpCircle, Edit, Save, Image, Settings, Ruler, X, Facebook, Instagram, Twitter, Truck, FileText } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { useAuth } from "../../contexts/AuthContext";
import { API } from "../../config";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Separator } from "../../components/ui/separator";
import { AdminLayout } from "./AdminCore";

// Admin Categories Page
// Flatten tree to array with depth for iterative rendering
function flattenTree(nodes, depth) {
  const result = [];
  for (const node of nodes) {
    result.push({ ...node, depth: depth || 0 });
    if (node.children && node.children.length > 0) {
      result.push(...flattenTree(node.children, (depth || 0) + 1));
    }
  }
  return result;
}

const CategoryTreeList = ({ tree, onAddSub, onEdit, onDelete }) => {
  const items = flattenTree(tree, 0);
  return items.map((cat) => (
    <div key={cat.id} data-testid={`category-tree-${cat.id}`} className="flex items-center gap-3 py-3 px-4 border-b border-[#E5E5E5] hover:bg-[#F5F5F5] transition-colors" style={{ paddingLeft: `${16 + cat.depth * 28}px` }}>
      {cat.depth > 0 && <span className="text-gray-300 text-xs">&ndash;</span>}
      {cat.image_url ? (
        <img src={cat.image_url} alt="" className="w-10 h-10 object-cover border flex-shrink-0" />
      ) : (
        <div className="w-10 h-10 bg-[#F5F5F5] border flex items-center justify-center flex-shrink-0">
          <FolderOpen className="w-5 h-5 text-gray-400" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="font-bold text-sm truncate">{cat.name}</p>
        <p className="text-xs text-gray-400">{cat.product_count || 0} products{cat.description ? ` · ${cat.description.slice(0, 40)}` : ""}</p>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <button onClick={() => onAddSub(cat.id)} className="text-xs text-[#FF6B00] font-bold px-2 py-1 hover:bg-[#FFF7ED]" title="Add sub-category" data-testid={`add-sub-${cat.id}`}>
          <Plus className="w-4 h-4" />
        </button>
        <button onClick={() => onEdit(cat)} className="text-xs text-gray-500 font-bold px-2 py-1 hover:bg-gray-100">Edit</button>
        <button onClick={() => onDelete(cat.id)} className="text-xs text-red-500 font-bold px-2 py-1 hover:bg-red-50">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  ));
};

const AdminCategories = () => {
  const { authAxios } = useAuth();
  const [categories, setCategories] = useState([]);
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editCategory, setEditCategory] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", image_url: "", parent_id: "" });

  const fetchData = useCallback(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/categories`),
      axios.get(`${API}/categories/tree`)
    ])
      .then(([flatRes, treeRes]) => {
        setCategories(flatRes.data);
        setTree(treeRes.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = (parentId = "") => {
    setForm({ name: "", description: "", image_url: "", parent_id: parentId });
    setEditCategory(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...form, parent_id: form.parent_id || null };
      if (editCategory) {
        await authAxios.put(`/categories/${editCategory.id}`, data);
        toast.success("Category updated!");
      } else {
        await authAxios.post("/categories", data);
        toast.success("Category created!");
      }
      setShowForm(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error saving category");
    }
  };

  const handleDelete = async (id) => {
    const hasChildren = categories.some((c) => c.parent_id === id);
    if (hasChildren) { toast.error("Delete sub-categories first"); return; }
    if (!window.confirm("Delete this category?")) return;
    try {
      await authAxios.delete(`/categories/${id}`);
      toast.success("Category deleted");
      fetchData();
    } catch (err) { toast.error("Error deleting category"); }
  };

  const openEdit = (category) => {
    setEditCategory(category);
    setForm({ name: category.name, description: category.description, image_url: category.image_url, parent_id: category.parent_id || "" });
    setShowForm(true);
  };

  const addSubCategory = (parentId) => {
    resetForm(parentId);
    setShowForm(true);
  };

  // Get flat list of parent options for select (exclude self and descendants)
  const getParentOptions = (excludeId = null) => {
    const excluded = new Set();
    if (excludeId) {
      const findDescendants = (pid) => {
        categories.filter((c) => c.parent_id === pid).forEach((c) => { excluded.add(c.id); findDescendants(c.id); });
      };
      excluded.add(excludeId);
      findDescendants(excludeId);
    }
    return categories.filter((c) => !excluded.has(c.id));
  };

  const getCategoryPath = (catId) => {
    const parts = [];
    let current = categories.find((c) => c.id === catId);
    while (current) {
      parts.unshift(current.name);
      current = current.parent_id ? categories.find((c) => c.id === current.parent_id) : null;
    }
    return parts.join(" / ");
  };

  return (
    <AdminLayout>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-black uppercase tracking-tight" data-testid="admin-categories-title">Categories</h1>
        <Button onClick={() => { resetForm(); setShowForm(true); }} className="btn-primary flex items-center gap-2" data-testid="add-category-button">
          <Plus className="w-4 h-4" /> Add Category
        </Button>
      </div>

      {/* Category Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-2xl font-black uppercase">{editCategory ? "Edit Category" : "Add Category"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="font-bold">Parent Category</Label>
              <Select value={form.parent_id || "none"} onValueChange={(v) => setForm({ ...form, parent_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1" data-testid="parent-category-select">
                  <SelectValue placeholder="None (Top Level)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (Top Level)</SelectItem>
                  {getParentOptions(editCategory?.id).map((c) => (
                    <SelectItem key={c.id} value={c.id}>{getCategoryPath(c.id)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-bold">Category Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1" data-testid="category-name-input" />
            </div>
            <div>
              <Label className="font-bold">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" rows={3} />
            </div>
            <div>
              <Label className="font-bold">Image URL</Label>
              <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://example.com/category.jpg" className="mt-1" />
            </div>
            <div className="flex gap-4">
              <Button type="button" onClick={() => setShowForm(false)} className="btn-outline flex-1">Cancel</Button>
              <Button type="submit" className="btn-primary flex-1" data-testid="save-category-button">{editCategory ? "Update" : "Create"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Category Tree */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner" /></div>
      ) : tree.length === 0 ? (
        <div className="text-center py-24 bg-white border-2 border-[#0A0A0A]">
          <FolderOpen className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-bold mb-2">No categories yet</h3>
          <p className="text-gray-500">Create your first category to organize products</p>
        </div>
      ) : (
        <div className="border-2 border-[#0A0A0A] bg-white overflow-hidden">
          <div className="bg-[#0A0A0A] text-white px-4 py-3 flex items-center gap-3">
            <FolderOpen className="w-5 h-5 text-[#FF6B00]" />
            <span className="font-bold uppercase text-sm">Category Tree</span>
            <span className="text-gray-400 text-xs ml-auto">{categories.length} total</span>
          </div>
          <CategoryTreeList tree={tree} onAddSub={addSubCategory} onEdit={openEdit} onDelete={handleDelete} />
        </div>
      )}
    </AdminLayout>
  );
};

// Admin Orders Page
const AdminOrders = () => {
  const { authAxios } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [trackingForm, setTrackingForm] = useState({
    status: "",
    location: "",
    description: "",
    carrier: "",
    tracking_number: ""
  });

  const fetchData = useCallback(() => {
    setLoading(true);
    authAxios.get("/orders")
      .then((res) => setOrders(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [authAxios]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateStatus = async (orderId, status) => {
    try {
      await authAxios.put(`/orders/${orderId}/status?status=${status}`);
      toast.success("Order status updated");
      fetchData();
    } catch (err) {
      toast.error("Error updating status");
    }
  };

  const updateTracking = async (orderId) => {
    try {
      await authAxios.put(`/orders/${orderId}/tracking`, trackingForm);
      toast.success("Tracking updated");
      setSelectedOrder(null);
      setTrackingForm({ status: "", location: "", description: "", carrier: "", tracking_number: "" });
      fetchData();
    } catch (err) {
      toast.error("Error updating tracking");
    }
  };

  const formatPrice = (price) => `₦${price.toLocaleString()}`;
  const formatDate = (date) => new Date(date).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" });

  return (
    <AdminLayout>
      <h1 className="text-3xl font-black uppercase tracking-tight mb-8" data-testid="admin-orders-title">Orders</h1>

      {/* Tracking Update Dialog */}
      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-2xl font-black uppercase">Update Tracking</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-bold">Carrier</Label>
                <Input
                  value={trackingForm.carrier}
                  onChange={(e) => setTrackingForm({ ...trackingForm, carrier: e.target.value })}
                  placeholder="DHL, FedEx, etc."
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="font-bold">Tracking Number</Label>
                <Input
                  value={trackingForm.tracking_number}
                  onChange={(e) => setTrackingForm({ ...trackingForm, tracking_number: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label className="font-bold">Status</Label>
              <Select value={trackingForm.status} onValueChange={(v) => setTrackingForm({ ...trackingForm, status: v })}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Picked up">Picked up</SelectItem>
                  <SelectItem value="In transit">In transit</SelectItem>
                  <SelectItem value="At customs">At customs</SelectItem>
                  <SelectItem value="Cleared customs">Cleared customs</SelectItem>
                  <SelectItem value="Out for delivery">Out for delivery</SelectItem>
                  <SelectItem value="Delivered">Delivered</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-bold">Location</Label>
              <Input
                value={trackingForm.location}
                onChange={(e) => setTrackingForm({ ...trackingForm, location: e.target.value })}
                placeholder="London, UK / Lagos, Nigeria"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="font-bold">Description</Label>
              <Textarea
                value={trackingForm.description}
                onChange={(e) => setTrackingForm({ ...trackingForm, description: e.target.value })}
                placeholder="Package has been picked up from warehouse"
                className="mt-1"
                rows={3}
              />
            </div>
            <div className="flex gap-4">
              <Button onClick={() => setSelectedOrder(null)} className="btn-outline flex-1">Cancel</Button>
              <Button onClick={() => updateTracking(selectedOrder)} className="btn-primary flex-1" data-testid="update-tracking-button">
                Update Tracking
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Orders Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner" />
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-24 bg-white border-2 border-[#0A0A0A]">
          <ClipboardList className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-bold mb-2">No orders yet</h3>
          <p className="text-gray-500">Orders will appear here once customers make purchases</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => (
            <Card key={order.id} className="border-2 border-[#0A0A0A]" data-testid={`order-card-${order.id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
                  <div>
                    <p className="font-black text-lg">{order.order_number}</p>
                    <p className="text-sm text-gray-500">{order.customer_email} • {formatDate(order.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`badge-${order.status}`}>{order.status}</span>
                    <span className={order.payment_status === "paid" ? "text-green-600 font-bold" : "text-yellow-600 font-bold"}>
                      {order.payment_status}
                    </span>
                    <span className="font-black text-xl text-[#FF6B00]">{formatPrice(order.total)}</span>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-sm font-bold mb-2">Items Ordered</p>
                    <div className="space-y-1">
                      {order.items.map((item, idx) => (
                        <p key={`${item.product_id}-${item.selected_size || idx}`} className="text-sm">
                          {item.name}{item.selected_size ? ` (${item.selected_size})` : ""} x {item.quantity} - {formatPrice(item.total)}
                        </p>
                      ))}
                    </div>
                    {order.discount > 0 && (
                      <p className="text-sm text-green-600 font-bold mt-1">Discount: -{formatPrice(order.discount)} {order.coupon_code && `(${order.coupon_code})`}</p>
                    )}
                    <p className="text-sm font-bold mt-2">Subtotal: {formatPrice(order.subtotal)} | Shipping: {formatPrice(order.shipping_cost)} | <span className="text-[#FF6B00]">Total: {formatPrice(order.total)}</span></p>
                  </div>
                  <div>
                    <p className="text-sm font-bold mb-2">Customer & Shipping Details</p>
                    <div className="bg-[#F5F5F5] p-3 space-y-1 text-sm">
                      <p><span className="font-bold">Name:</span> {order.shipping_address.full_name}</p>
                      <p><span className="font-bold">Email:</span> {order.customer_email}</p>
                      <p><span className="font-bold">Phone:</span> {order.shipping_address.phone || "Not provided"}</p>
                      <p><span className="font-bold">Address:</span> {order.shipping_address.address_line1}{order.shipping_address.address_line2 ? `, ${order.shipping_address.address_line2}` : ""}</p>
                      <p><span className="font-bold">City:</span> {order.shipping_address.city}</p>
                      <p><span className="font-bold">State:</span> {order.shipping_address.state}</p>
                      {order.shipping_address.postal_code && <p><span className="font-bold">Postal Code:</span> {order.shipping_address.postal_code}</p>}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Select value={order.status} onValueChange={(v) => updateStatus(order.id, v)}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="processing">Processing</SelectItem>
                      <SelectItem value="shipped">Shipped</SelectItem>
                      <SelectItem value="delivered">Delivered</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={() => setSelectedOrder(order.id)}
                    className="btn-outline flex items-center gap-2"
                    data-testid={`update-tracking-${order.id}`}
                  >
                    <Truck className="w-4 h-4" /> Update Tracking
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </AdminLayout>
  );
};

// Admin FAQs Management
const AdminFAQs = () => {
  const { authAxios } = useAuth();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editFaq, setEditFaq] = useState(null);
  const [form, setForm] = useState({ question: "", answer: "", order: 0 });

  const fetchData = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/faqs`)
      .then((res) => setFaqs(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editFaq) {
        await authAxios.put(`/faqs/${editFaq.id}`, form);
        toast.success("FAQ updated!");
      } else {
        await authAxios.post("/faqs", { ...form, order: faqs.length + 1 });
        toast.success("FAQ created!");
      }
      setShowForm(false);
      setEditFaq(null);
      setForm({ question: "", answer: "", order: 0 });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error saving FAQ");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this FAQ?")) return;
    try {
      await authAxios.delete(`/faqs/${id}`);
      toast.success("FAQ deleted");
      fetchData();
    } catch (err) {
      toast.error("Error deleting FAQ");
    }
  };

  const openEdit = (faq) => {
    setEditFaq(faq);
    setForm({ question: faq.question, answer: faq.answer, order: faq.order });
    setShowForm(true);
  };

  return (
    <AdminLayout>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-black uppercase tracking-tight" data-testid="admin-faqs-title">FAQs</h1>
        <Button onClick={() => { setForm({ question: "", answer: "", order: faqs.length + 1 }); setEditFaq(null); setShowForm(true); }} className="btn-primary flex items-center gap-2" data-testid="add-faq-button">
          <Plus className="w-4 h-4" /> Add FAQ
        </Button>
      </div>

      {/* FAQ Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-2xl font-black uppercase">{editFaq ? "Edit FAQ" : "Add FAQ"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="font-bold">Question</Label>
              <Input
                value={form.question}
                onChange={(e) => setForm({ ...form, question: e.target.value })}
                required
                className="mt-1"
                placeholder="What is your shipping policy?"
              />
            </div>
            <div>
              <Label className="font-bold">Answer</Label>
              <Textarea
                value={form.answer}
                onChange={(e) => setForm({ ...form, answer: e.target.value })}
                required
                className="mt-1"
                rows={4}
                placeholder="Detailed answer to the question..."
              />
            </div>
            <div>
              <Label className="font-bold">Display Order</Label>
              <Input
                type="number"
                value={form.order}
                onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) })}
                className="mt-1"
              />
            </div>
            <div className="flex gap-4">
              <Button type="button" onClick={() => setShowForm(false)} className="btn-outline flex-1">Cancel</Button>
              <Button type="submit" className="btn-primary flex-1">{editFaq ? "Update" : "Create"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* FAQs List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner" />
        </div>
      ) : faqs.length === 0 ? (
        <div className="text-center py-24 bg-white border-2 border-[#0A0A0A]">
          <HelpCircle className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-bold mb-2">No FAQs yet</h3>
          <p className="text-gray-500">Add frequently asked questions to help your customers</p>
        </div>
      ) : (
        <div className="space-y-4">
          {faqs.map((faq, idx) => (
            <Card key={faq.id} className="border-2 border-[#0A0A0A]">
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <p className="font-bold text-lg flex items-center gap-2">
                      <span className="text-[#FF6B00]">{idx + 1}.</span>
                      {faq.question}
                    </p>
                    <p className="text-gray-600 mt-2">{faq.answer}</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => openEdit(faq)} className="text-blue-600 hover:text-blue-800">Edit</button>
                    <button onClick={() => handleDelete(faq.id)} className="text-red-600 hover:text-red-800">Delete</button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </AdminLayout>
  );
};

// Admin Coupons Management
const AdminCoupons = () => {
  const { authAxios } = useAuth();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editCoupon, setEditCoupon] = useState(null);
  const [form, setForm] = useState({ code: "", discount_amount: "", min_order_amount: "0", max_uses: "0", expires_at: "", is_active: true });

  const fetchData = useCallback(() => {
    setLoading(true);
    authAxios.get("/coupons")
      .then((res) => setCoupons(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [authAxios]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setForm({ code: "", discount_amount: "", min_order_amount: "0", max_uses: "0", expires_at: "", is_active: true });
    setEditCoupon(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        code: form.code.toUpperCase().trim(),
        discount_amount: parseFloat(form.discount_amount),
        min_order_amount: parseFloat(form.min_order_amount) || 0,
        max_uses: parseInt(form.max_uses) || 0,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        is_active: form.is_active
      };
      if (editCoupon) {
        await authAxios.put(`/coupons/${editCoupon.id}`, data);
        toast.success("Coupon updated!");
      } else {
        await authAxios.post("/coupons", data);
        toast.success("Coupon created!");
      }
      setShowForm(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error saving coupon");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this coupon?")) return;
    try {
      await authAxios.delete(`/coupons/${id}`);
      toast.success("Coupon deleted");
      fetchData();
    } catch (err) { toast.error("Error deleting coupon"); }
  };

  const openEdit = (coupon) => {
    setEditCoupon(coupon);
    setForm({
      code: coupon.code,
      discount_amount: coupon.discount_amount.toString(),
      min_order_amount: (coupon.min_order_amount || 0).toString(),
      max_uses: (coupon.max_uses || 0).toString(),
      expires_at: coupon.expires_at ? coupon.expires_at.slice(0, 16) : "",
      is_active: coupon.is_active !== false
    });
    setShowForm(true);
  };

  const toggleActive = async (coupon) => {
    try {
      await authAxios.put(`/coupons/${coupon.id}`, { is_active: !coupon.is_active });
      fetchData();
      toast.success(`Coupon ${coupon.is_active ? "deactivated" : "activated"}`);
    } catch (err) { toast.error("Error updating coupon"); }
  };

  const formatPrice = (p) => `₦${(p || 0).toLocaleString()}`;

  return (
    <AdminLayout>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <h1 className="text-3xl font-black uppercase tracking-tight" data-testid="admin-coupons-title">Coupons</h1>
        <Button onClick={() => { resetForm(); setShowForm(true); }} className="btn-primary flex items-center gap-2" data-testid="add-coupon-button">
          <Plus className="w-4 h-4" /> Create Coupon
        </Button>
      </div>

      {/* Coupon Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black uppercase">{editCoupon ? "Edit Coupon" : "Create Coupon"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="font-bold">Coupon Code</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} required placeholder="e.g. SAVE5K" className="mt-1 uppercase" disabled={!!editCoupon} data-testid="coupon-code-input" />
            </div>
            <div>
              <Label className="font-bold">Discount Amount (₦)</Label>
              <Input type="number" value={form.discount_amount} onChange={(e) => setForm({ ...form, discount_amount: e.target.value })} required min="1" step="0.01" className="mt-1" data-testid="coupon-discount-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-bold">Min Order Amount (₦)</Label>
                <Input type="number" value={form.min_order_amount} onChange={(e) => setForm({ ...form, min_order_amount: e.target.value })} min="0" className="mt-1" data-testid="coupon-min-order-input" />
                <p className="text-xs text-gray-400 mt-1">0 = no minimum</p>
              </div>
              <div>
                <Label className="font-bold">Max Uses</Label>
                <Input type="number" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} min="0" className="mt-1" data-testid="coupon-max-uses-input" />
                <p className="text-xs text-gray-400 mt-1">0 = unlimited</p>
              </div>
            </div>
            <div>
              <Label className="font-bold">Expiry Date</Label>
              <Input type="datetime-local" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="mt-1" data-testid="coupon-expiry-input" />
              <p className="text-xs text-gray-400 mt-1">Leave empty for no expiry</p>
            </div>
            <div className="flex gap-4">
              <Button type="button" onClick={() => { setShowForm(false); resetForm(); }} className="btn-outline flex-1">Cancel</Button>
              <Button type="submit" className="btn-primary flex-1" data-testid="save-coupon-button">{editCoupon ? "Update Coupon" : "Create Coupon"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Coupons Table */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner" /></div>
      ) : coupons.length === 0 ? (
        <div className="text-center py-24 bg-white border-2 border-[#0A0A0A]">
          <Gift className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-bold mb-2">No coupons yet</h3>
          <p className="text-gray-500 mb-6">Create your first discount coupon</p>
        </div>
      ) : (
        <div className="border-2 border-[#0A0A0A] overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-[#0A0A0A] text-white text-left">
                <th className="p-4 font-bold uppercase text-sm">Code</th>
                <th className="p-4 font-bold uppercase text-sm">Discount</th>
                <th className="p-4 font-bold uppercase text-sm hidden md:table-cell">Min Order</th>
                <th className="p-4 font-bold uppercase text-sm hidden md:table-cell">Uses</th>
                <th className="p-4 font-bold uppercase text-sm hidden md:table-cell">Expires</th>
                <th className="p-4 font-bold uppercase text-sm">Status</th>
                <th className="p-4 font-bold uppercase text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {coupons.map((coupon) => {
                const expired = coupon.expires_at && new Date(coupon.expires_at) < new Date();
                const maxedOut = coupon.max_uses > 0 && coupon.used_count >= coupon.max_uses;
                return (
                  <tr key={coupon.id} className="border-t border-[#E5E5E5] hover:bg-[#F5F5F5]" data-testid={`coupon-row-${coupon.code}`}>
                    <td className="p-4 font-black text-[#FF6B00]">{coupon.code}</td>
                    <td className="p-4 font-bold">{formatPrice(coupon.discount_amount)}</td>
                    <td className="p-4 hidden md:table-cell">{coupon.min_order_amount > 0 ? formatPrice(coupon.min_order_amount) : "None"}</td>
                    <td className="p-4 hidden md:table-cell">{coupon.used_count || 0}{coupon.max_uses > 0 ? `/${coupon.max_uses}` : "/∞"}</td>
                    <td className="p-4 hidden md:table-cell text-sm">{coupon.expires_at ? new Date(coupon.expires_at).toLocaleDateString() : "Never"}</td>
                    <td className="p-4">
                      <span className={`text-xs font-bold px-2 py-1 ${expired ? "bg-red-100 text-red-700" : maxedOut ? "bg-yellow-100 text-yellow-700" : coupon.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}`}>
                        {expired ? "Expired" : maxedOut ? "Maxed" : coupon.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(coupon)} className="text-[#FF6B00] font-bold text-sm hover:underline">Edit</button>
                        <button onClick={() => toggleActive(coupon)} className="text-gray-500 font-bold text-sm hover:underline">{coupon.is_active ? "Disable" : "Enable"}</button>
                        <button onClick={() => handleDelete(coupon.id)} className="text-red-500 font-bold text-sm hover:underline">Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
};

// Admin Content & SEO Editor
const AdminContent = () => {
  const { authAxios } = useAuth();
  const [activeTab, setActiveTab] = useState("seo");
  const [seoSettings, setSeoSettings] = useState({});
  const [termsContent, setTermsContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // SEO Form states
  const [seoForm, setSeoForm] = useState({
    page: "home",
    title: "",
    description: "",
    keywords: ""
  });

  // Social Links states
  const [socialLinks, setSocialLinks] = useState({
    facebook: "https://facebook.com/ukok.ng",
    instagram: "https://instagram.com/ukok.ng",
    twitter: "https://twitter.com/ukok_ng",
    whatsapp: "2348000000000",
    tiktok: "https://tiktok.com/@ukok.ng"
  });

  const pages = [
    { value: "home", label: "Home Page" },
    { value: "products", label: "Products Page" },
    { value: "checkout", label: "Checkout Page" },
    { value: "faq", label: "FAQ Page" },
    { value: "terms", label: "Terms Page" }
  ];

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [seoRes, termsRes] = await Promise.all([
        axios.get(`${API}/seo`),
        axios.get(`${API}/terms`)
      ]);
      setSeoSettings(seoRes.data);
      setTermsContent(termsRes.data.content || "");
      
      // Set initial SEO form values
      const homeSeo = seoRes.data.home || {};
      setSeoForm({
        page: "home",
        title: homeSeo.title || "UKOK - Premium UK Products for Nigeria",
        description: homeSeo.description || "Shop premium UK products delivered to Nigeria",
        keywords: homeSeo.keywords || "UK products, Nigeria, dropshipping, online shopping"
      });
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const loadPageSeo = (page) => {
    const pageSeo = seoSettings[page] || {};
    setSeoForm({
      page,
      title: pageSeo.title || "",
      description: pageSeo.description || "",
      keywords: pageSeo.keywords || ""
    });
  };

  const saveSeo = async () => {
    setSaving(true);
    try {
      await authAxios.put(`/seo/${seoForm.page}`, seoForm);
      toast.success("SEO settings saved! Changes will appear on the live site.");
      fetchData();
    } catch (err) {
      toast.error("Error saving SEO settings");
    }
    setSaving(false);
  };

  const saveTerms = async () => {
    setSaving(true);
    try {
      await authAxios.put(`/terms?content=${encodeURIComponent(termsContent)}`);
      toast.success("Terms & Conditions updated! Changes are now live.");
    } catch (err) {
      toast.error("Error saving terms");
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center py-12">
          <div className="spinner" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <h1 className="text-3xl font-black uppercase tracking-tight mb-8" data-testid="admin-content-title">Content & SEO Editor</h1>
      
      {/* Tabs */}
      <div className="flex gap-2 mb-8 flex-wrap">
        <button
          onClick={() => setActiveTab("seo")}
          className={`px-6 py-3 font-bold transition-colors ${activeTab === "seo" ? "bg-[#0A0A0A] text-white" : "bg-[#F5F5F5] hover:bg-[#E5E5E5]"}`}
        >
          SEO Settings
        </button>
        <button
          onClick={() => setActiveTab("social")}
          className={`px-6 py-3 font-bold transition-colors ${activeTab === "social" ? "bg-[#0A0A0A] text-white" : "bg-[#F5F5F5] hover:bg-[#E5E5E5]"}`}
        >
          Social Links
        </button>
        <button
          onClick={() => setActiveTab("terms")}
          className={`px-6 py-3 font-bold transition-colors ${activeTab === "terms" ? "bg-[#0A0A0A] text-white" : "bg-[#F5F5F5] hover:bg-[#E5E5E5]"}`}
        >
          Terms & Conditions
        </button>
      </div>

      {activeTab === "seo" && (
        <Card className="border-2 border-[#0A0A0A]">
          <CardHeader>
            <CardTitle className="font-black uppercase flex items-center gap-2">
              <Edit className="w-5 h-5" />
              SEO Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-green-50 border border-green-500 p-4 mb-4">
              <p className="text-green-700 text-sm flex items-center gap-2">
                <Check className="w-4 h-4" />
                Changes are automatically published to the live site when you save.
              </p>
            </div>

            <div>
              <Label className="font-bold">Select Page</Label>
              <Select value={seoForm.page} onValueChange={(v) => { loadPageSeo(v); }}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {pages.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="font-bold">Page Title (appears in browser tab & search results)</Label>
              <Input
                value={seoForm.title}
                onChange={(e) => setSeoForm({ ...seoForm, title: e.target.value })}
                className="mt-1"
                placeholder="UKOK - Premium UK Products for Nigeria"
              />
              <p className="text-xs text-gray-500 mt-1">{seoForm.title.length}/60 characters (recommended)</p>
            </div>

            <div>
              <Label className="font-bold">Meta Description (shown in search results)</Label>
              <Textarea
                value={seoForm.description}
                onChange={(e) => setSeoForm({ ...seoForm, description: e.target.value })}
                className="mt-1"
                rows={3}
                placeholder="Shop premium UK products delivered to Nigeria..."
              />
              <p className="text-xs text-gray-500 mt-1">{seoForm.description.length}/160 characters (recommended)</p>
            </div>

            <div>
              <Label className="font-bold">Keywords (comma-separated)</Label>
              <Input
                value={seoForm.keywords}
                onChange={(e) => setSeoForm({ ...seoForm, keywords: e.target.value })}
                className="mt-1"
                placeholder="UK products, Nigeria, online shopping, dropshipping"
              />
            </div>

            <Button onClick={saveSeo} className="btn-primary flex items-center gap-2" disabled={saving}>
              <Save className="w-4 h-4" />
              {saving ? "Saving..." : "Save & Publish SEO"}
            </Button>
          </CardContent>
        </Card>
      )}

      {activeTab === "social" && (
        <Card className="border-2 border-[#0A0A0A]">
          <CardHeader>
            <CardTitle className="font-black uppercase flex items-center gap-2">
              <Share2 className="w-5 h-5" />
              Social Media Links
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-blue-50 border border-blue-500 p-4 mb-4">
              <p className="text-blue-700 text-sm">
                Update your social media links. These appear in the website footer and are used for sharing features.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label className="font-bold flex items-center gap-2">
                  <Facebook className="w-4 h-4 text-blue-600" /> Facebook
                </Label>
                <Input
                  value={socialLinks.facebook}
                  onChange={(e) => setSocialLinks({ ...socialLinks, facebook: e.target.value })}
                  className="mt-1"
                  placeholder="https://facebook.com/yourpage"
                />
              </div>

              <div>
                <Label className="font-bold flex items-center gap-2">
                  <Instagram className="w-4 h-4 text-pink-600" /> Instagram
                </Label>
                <Input
                  value={socialLinks.instagram}
                  onChange={(e) => setSocialLinks({ ...socialLinks, instagram: e.target.value })}
                  className="mt-1"
                  placeholder="https://instagram.com/yourhandle"
                />
              </div>

              <div>
                <Label className="font-bold flex items-center gap-2">
                  <Twitter className="w-4 h-4 text-blue-400" /> Twitter / X
                </Label>
                <Input
                  value={socialLinks.twitter}
                  onChange={(e) => setSocialLinks({ ...socialLinks, twitter: e.target.value })}
                  className="mt-1"
                  placeholder="https://twitter.com/yourhandle"
                />
              </div>

              <div>
                <Label className="font-bold flex items-center gap-2">
                  <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  WhatsApp (number only)
                </Label>
                <Input
                  value={socialLinks.whatsapp}
                  onChange={(e) => setSocialLinks({ ...socialLinks, whatsapp: e.target.value })}
                  className="mt-1"
                  placeholder="2348012345678"
                />
                <p className="text-xs text-gray-500 mt-1">Enter number with country code, no + or spaces</p>
              </div>

              <div>
                <Label className="font-bold flex items-center gap-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/></svg>
                  TikTok
                </Label>
                <Input
                  value={socialLinks.tiktok}
                  onChange={(e) => setSocialLinks({ ...socialLinks, tiktok: e.target.value })}
                  className="mt-1"
                  placeholder="https://tiktok.com/@yourhandle"
                />
              </div>
            </div>

            <Button onClick={() => { toast.success("Social links saved! Update the footer code to use dynamic links."); }} className="btn-primary flex items-center gap-2">
              <Save className="w-4 h-4" />
              Save Social Links
            </Button>

            <div className="bg-yellow-50 border border-yellow-500 p-4">
              <p className="text-yellow-700 text-sm">
                <strong>Note:</strong> To update these links on the live site, you'll need to edit the footer component directly. These settings are saved for reference.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === "terms" && (
        <Card className="border-2 border-[#0A0A0A]">
          <CardHeader>
            <CardTitle className="font-black uppercase flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Terms & Conditions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-green-50 border border-green-500 p-4 mb-4">
              <p className="text-green-700 text-sm flex items-center gap-2">
                <Check className="w-4 h-4" />
                Use Markdown formatting: # for headings, ## for subheadings, ** for bold.
              </p>
            </div>

            <div>
              <Label className="font-bold">Terms & Conditions Content</Label>
              <Textarea
                value={termsContent}
                onChange={(e) => setTermsContent(e.target.value)}
                className="mt-1 font-mono text-sm"
                rows={20}
                placeholder="# Terms and Conditions&#10;&#10;**Last Updated: January 2026**&#10;&#10;## 1. General Terms..."
              />
            </div>

            <Button onClick={saveTerms} className="btn-primary flex items-center gap-2" disabled={saving}>
              <Save className="w-4 h-4" />
              {saving ? "Saving..." : "Save & Publish Terms"}
            </Button>
          </CardContent>
        </Card>
      )}
    </AdminLayout>
  );
};


export { AdminCategories, AdminOrders, AdminFAQs, AdminCoupons, AdminContent };
